import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Icon } from '../icon/icon';
import { type Lang } from '../../../core/data/languages.config';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { LanguageService } from '../../../core/services/language.service';

/**
 * Language picker.
 *
 * The trigger is the active language's own code, set in the mono face the rest
 * of the chrome uses for machine voice; it opens a listbox of every supported
 * language. Built as a dropdown rather than a segmented control so it stays the
 * same size whether the site ships two languages or twelve — the list scrolls,
 * the trigger does not grow.
 *
 * **No flags.** A flag is a country, not a language: `gb` for English says
 * nothing to a reader in Dublin or Lagos, and French is not only France. The
 * code and the endonym name the language itself, which is what is being chosen.
 *
 * Each option shows its own endonym, so the picker stays readable to someone
 * who cannot read the language currently active.
 */
@Component({
  selector: 'av-lang-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, Icon],
  template: `
    <div class="ls">
      <button
        #trigger
        type="button"
        class="ls__trigger"
        [class.is-open]="open()"
        [attr.aria-label]="('lang.switch' | translate) + ': ' + current().endonym"
        [attr.aria-expanded]="open()"
        aria-haspopup="listbox"
        [attr.title]="'lang.switch' | translate"
        (click)="toggle()"
        (keydown)="onTriggerKeydown($event)"
      >
        <span class="ls__current t-mono" aria-hidden="true">{{ current().code }}</span>
        <span class="ls__caret" aria-hidden="true"><av-icon name="chevron-down" [size]="12" /></span>
      </button>

      @if (open()) {
        <ul
          class="ls__menu"
          role="listbox"
          [attr.aria-label]="'lang.switch' | translate"
          [attr.aria-activedescendant]="'ls-opt-' + languages[focused()].code"
        >
          @for (option of languages; track option.code; let i = $index) {
            <li
              #option
              class="ls__option"
              role="option"
              [id]="'ls-opt-' + option.code"
              [class.is-active]="option.code === current().code"
              [class.is-focused]="i === focused()"
              [attr.aria-selected]="option.code === current().code"
              [attr.lang]="option.code"
              tabindex="-1"
              (click)="choose(option.code)"
              (mouseenter)="focused.set(i)"
              (keydown)="onMenuKeydown($event)"
            >
              <span class="ls__name">{{ option.endonym }}</span>
              <span class="ls__code t-mono">{{ option.code }}</span>
              @if (option.code === current().code) {
                <span class="ls__check" aria-hidden="true">
                  <av-icon name="check" [size]="13" [weight]="2.6" />
                </span>
              }
            </li>
          }
        </ul>
      }
    </div>
  `,
  styleUrl: './lang-switcher.scss',
})
export class LangSwitcher {
  private readonly language = inject(LanguageService);
  private readonly analytics = inject(AnalyticsService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly options = viewChildren<ElementRef<HTMLElement>>('option');

  protected readonly languages = this.language.supported;
  protected readonly open = signal(false);
  protected readonly focused = signal(0);

  protected readonly current = computed(() => {
    const code = this.language.lang();
    return this.languages.find((l) => l.code === code) ?? this.languages[0];
  });

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.close(false);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) this.close(true);
  }

  protected toggle(): void {
    this.open() ? this.close(false) : this.reveal();
  }

  protected choose(code: Lang): void {
    const from = this.language.lang();
    if (code !== from) this.analytics.languageChanged(from, code);

    void this.language.use(code);
    this.close(true);
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    this.reveal(event.key === 'ArrowUp' ? this.languages.length - 1 : 0);
  }

  protected onMenuKeydown(event: KeyboardEvent): void {
    const last = this.languages.length - 1;
    const move = (index: number) => {
      event.preventDefault();
      this.focused.set(index);
      this.focusOption(index);
    };

    switch (event.key) {
      case 'ArrowDown':
        return move(this.focused() === last ? 0 : this.focused() + 1);
      case 'ArrowUp':
        return move(this.focused() === 0 ? last : this.focused() - 1);
      case 'Home':
        return move(0);
      case 'End':
        return move(last);
      case 'Enter':
      case ' ':
        event.preventDefault();
        return this.choose(this.languages[this.focused()].code);
      case 'Tab':
        return this.close(false);
    }
  }

  /** Opens the list with focus on the given index, or on the active language. */
  private reveal(index?: number): void {
    const start = index ?? this.languages.findIndex((l) => l.code === this.current().code);
    this.focused.set(Math.max(0, start));
    this.open.set(true);
    // The list renders on the next frame; move focus once it exists.
    requestAnimationFrame(() => this.focusOption(this.focused()));
  }

  private focusOption(index: number): void {
    this.options()[index]?.nativeElement.focus();
  }

  private close(returnFocus: boolean): void {
    this.open.set(false);
    if (returnFocus) {
      (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>('.ls__trigger')?.focus();
    }
  }
}
